// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import { toast } from 'sonner';

// Define a minimal Database type if the actual one isn't available
type Database = any;

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const supabaseServiceKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InFqaml3ZXpla3Ftd2Nqb29vbmZmIiwicm9sZSI6InNlcnZpY2Vfcm9sZSIsImlhdCI6MTc0MjkwMTQ0OSwiZXhwIjoyMDU4NDc3NDQ5fQ.Fmw3tfHCrLjWrZGUp6wY6mmPHofkulmVrUBsx7RAwTs';

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

// Create a single instance of the Supabase client with persistence
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    storageKey: 'h44s-auth',
    storage: window.localStorage,
    autoRefreshToken: true,
    detectSessionInUrl: true
  }
});

// Create a single instance of the admin client
export const supabaseAdmin = createClient<Database>(
  supabaseUrl || 'https://qjjiwezekqmwcjooonff.supabase.co',
  supabaseServiceKey
);

// Helper functions for common tables with admin client
export const getTable = <T = any>(tableName: string, useAdmin: boolean = false) => {
  return (useAdmin ? supabaseAdmin : supabase).from(tableName as any) as any;
};

// Helper functions for common tables
export const profiles = (useAdmin: boolean = false) => getTable<{
  id: string;
  email: string;
  full_name: string | null;
  user_type: 'admin' | 'investor' | 'entrepreneur';
  created_at: string;
  updated_at: string;
}>('profiles', useAdmin);

export const investorMetrics = () => getTable<{
  id: string;
  total_investments: number;
  portfolio_value: number;
  avg_return_rate: number;
  dividends_received: number;
  viewed_profiles: number;
  active_deals: number;
  last_updated: string;
}>('investor_metrics');

export const companyUsers = () => getTable<{
  id: string;
  company_id: string;
  user_id: string;
  role: 'owner' | 'admin' | 'member';
  created_at: string;
}>('company_users');

export const businessProgress = () => getTable<{
  id: string;
  business_name: string;
  development_stage: string;
  description: string | null;
  funding_required: number;
  interested_investors: number;
  completion_percentage: number;
  industry_type: string | null;
  user_id: string;
  created_at: string;
  updated_at: string;
}>('business_progress');

export const fundingRequirements = () => getTable<{
  id: string;
  business_id: string;
  amount: number;
  next_step: string;
  fund_allocation_image: string | null;
  created_at: string;
  updated_at: string;
}>('funding_requirements');

export const platformMetrics = () => getTable<{
  id: string;
  metric_name: string;
  metric_value: string;
  metric_category: string;
  created_at: string;
  updated_at: string;
}>('platform_metrics');

export const investmentPerformance = () => getTable<{
  id: string;
  month: string;
  investments: number;
  portfolio_value: number;
  created_at: string | null;
  updated_at: string | null;
}>('investment_performance');

export const investmentSectors = () => getTable<{
  id: string;
  sector_name: string;
  allocation: number;
  color: string;
  created_at: string | null;
  updated_at: string | null;
}>('investment_sectors');

export const businesses = (useAdmin: boolean = false) => getTable<{
  id: string;
  business_name: string;
  company_name: string | null;
  tagline: string | null;
  description: string;
  logo_url: string | null;
  pitch_deck_url: string | null;
  development_stage: string;
  industry_type: string;
  website: string | null;
  contact_email: string | null;
  problem_statement: string | null;
  solution_description: string | null;
  market_size: string | null;
  funding_goal: number | null;
  investment_highlights: string[] | null;
  competitive_edge: string[] | null;
  target_segment: string | null;
  growth_drivers: string | null;
  team_members: any | null;
  business_model: any | null;
  traction: any | null;
  product_offerings: any | null;
  financial_projections: any | null;
  entity_type: string;
  status: string;
  user_id: string;
  created_at: string;
  updated_at: string;
}>('businesses', useAdmin);

export const investorDocuments = () => getTable<{
  id: string;
  user_id: string;
  document_name: string;
  document_type: string;
  file_path: string;
  file_size: number;
  is_approved: boolean | null;
  uploaded_at: string | null;
}>('investor_documents');

export const contactMessages = () => getTable<{
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  message: string;
  status: string;
  admin_response: string | null;
  response_sent_at: string | null;
  created_at: string;
}>('contact_messages');

export const chatMessages = () => getTable<{
  id: string;
  sender_id: string;
  receiver_id: string;
  message: string;
  is_read: boolean | null;
  created_at: string;
}>('chat_messages');

export const conversations = () => getTable<{
  id: string;
  title: string;
  participants: string[];
  conversation_type: string;
  last_message_at: string;
  created_at: string;
  updated_at: string;
}>('conversations');

export const messages = () => getTable<{
  id: string;
  conversation_id: string;
  sender_id: string;
  content: string;
  read_by: string[];
  created_at: string;
  updated_at: string;
}>('messages');

export const notifications = () => getTable<{
  id: string;
  user_id: string;
  type: string;
  title: string;
  content: string;
  read: boolean;
  link: string;
  metadata: any;
  created_at: string;
  updated_at: string;
}>('notifications');

export const userRoles = () => getTable<{
  id: string;
  user_id: string;
  role: string;
  created_at: string;
}>('user_roles');

// Add new helper functions for the new tables
export const chatSettings = () => getTable<Database['public']['Tables']['chat_settings']['Row']>('chat_settings');
export const knowledgeBase = () => getTable<Database['public']['Tables']['knowledge_base']['Row']>('knowledge_base');
export const localDocuments = () => getTable<Database['public']['Tables']['local_documents']['Row']>('local_documents');

// Add user_invites helper function
export const userInvites = () => getTable<{
  id: string;
  email: string;
  role: string;
  status: string;
  invited_by: string;
  created_at: string;
  expires_at: string | null;
}>('user_invites');

export const entrepreneurApprovals = () => getTable<{
  id: string;
  user_id: string;
  business_name: string;
  business_description: string | null;
  requested_at: string;
  status: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
  notes: string | null;
}>('entrepreneur_approvals');
